import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Trash2, Upload, Image as ImageIcon } from "lucide-react";

interface Props {
  produtoId: number;
  slot:      string;
  label:     string;
  hasImage:  boolean;
  onUpload:  (file: File) => void;
  onRemove:  () => void;
}

export function ProdutoImageSlot({ produtoId, slot, label, hasImage, onUpload, onRemove }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: blobUrl } = useQuery({
    queryKey: ["produto-imagem-blob", produtoId, slot],
    queryFn: async () => {
      const res = await apiClient.get(`/api/produtos/${produtoId}/imagens/${slot}`, { responseType: "blob" });
      return URL.createObjectURL(res.data as Blob);
    },
    enabled: hasImage,
    staleTime: Infinity,
  });

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium">{label}</p>
      <div className="relative aspect-square rounded-lg border border-dashed flex items-center justify-center bg-muted/30 overflow-hidden">
        {hasImage && blobUrl ? (
          <img src={blobUrl} alt={label} className="w-full h-full object-contain" />
        ) : (
          <div className="text-center text-muted-foreground">
            <ImageIcon className="h-8 w-8 mx-auto opacity-40" />
            <span className="text-xs">Sem foto</span>
          </div>
        )}
      </div>
      <div className="flex gap-1">
        <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => inputRef.current?.click()}>
          <Upload className="h-3.5 w-3.5 mr-1" /> {hasImage ? "Trocar" : "Enviar"}
        </Button>
        {hasImage && (
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onRemove} title="Remover">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
      />
    </div>
  );
}
